import { type Component, type ComputedRef, type Ref } from 'vue';
import type { MovementType, MovementTypeGroup } from '@/shared/api/experience';

export enum TMovementType {
  foot = 'foot',
  car = 'car',
  bicycle = 'bicycle',
  bus = 'bus',
  motorcycle = 'motorcycle',
  horseback = 'horseback',
  boat = 'boat',
  kayak = 'kayak',
  yacht = 'yacht',
  airplane = 'airplane',
  balloon = 'balloon',
  sup = 'sup',
  museum = 'museum',
  scooter = 'scooter',
  speedboat = 'speedboat',
  jetski = 'jetski',
  paraglider = 'paraglider',
  helicopter = 'helicopter',
  motorship = 'motorship',
  quadbike = 'quadbike',
  other = 'other',
  watership = 'watership',
  room = 'room',
}

export function isValidMovementType(key: string): key is TMovementType {
  return Object.values(TMovementType).includes(key as TMovementType);
}

export enum TMovementTypeGroup {
  bicycleScooter = 'bicycle_scooter',
  motorcycleQuadbike = 'motorcycle_quadbike',
  watership = 'watership',
  boatKayakSup = 'boat_kayak_sup',
  yachtSpeedboat = 'yacht_speedboat',
  balloonParaglider = 'balloon_paraglider',
  airplaneHelicopter = 'airplane_helicopter',
  roomMuseum = 'room_museum',
}

export enum IconName {
  Airplane = 'Airplane',
  Balloon = 'Balloon',
  Bike = 'Bike',
  Boat = 'Boat',
  Bus = 'Bus',
  Car = 'Car',
  Horseback = 'Horseback',
  Kayak = 'Kayak',
  Moto = 'Moto',
  Room = 'Room',
  Ship = 'Ship',
  Ufo = 'Ufo',
  Walk = 'Walk',
  Yacht = 'Yacht',
}

export type MovementItem = IMovementType | IMovementTypeGroup;
export type MovementCounts = Record<TMovementType, number>;

export type IconComponent = Record<IconName, () => Component>;

export type IMovementType = MovementType & {
  str: TMovementType;
  iconName: IconName | null;
  isSelected: boolean;
  labelPhrase: string;
};

export type ExpCardLabels = Record<TMovementType, MovementType['label_phrase']>;

export type IMovementTypeGroup = MovementTypeGroup & {
  str: TMovementTypeGroup;
  iconName: IconName | null;
  types: Array<TMovementType[keyof TMovementType]>;
  isSelected: boolean;
};

export type ComputedMovementTypeData = {
  movementTypes: ComputedRef<IMovementType[]>;
  movementTypeGroups: ComputedRef<IMovementTypeGroup[]>;
  movementTypesExpCardLabels: Ref<ExpCardLabels | null>;
};

export const iconMapping: Partial<Record<TMovementType & TMovementTypeGroup, IconName>> = {
  [TMovementType.foot]: IconName.Walk,
  [TMovementType.car]: IconName.Car,
  [TMovementType.bicycle]: IconName.Bike,
  [TMovementType.bus]: IconName.Bus,
  [TMovementType.motorcycle]: IconName.Moto,
  [TMovementTypeGroup.watership]: IconName.Boat,
  [TMovementTypeGroup.roomMuseum]: IconName.Room,
  [TMovementType.horseback]: IconName.Horseback,
  [TMovementType.motorship]: IconName.Ship,
  [TMovementType.kayak]: IconName.Kayak,
  [TMovementType.yacht]: IconName.Yacht,
  [TMovementType.airplane]: IconName.Airplane,
  [TMovementType.balloon]: IconName.Balloon,
  [TMovementTypeGroup.bicycleScooter]: IconName.Bike,
  [TMovementTypeGroup.motorcycleQuadbike]: IconName.Moto,
  [TMovementTypeGroup.balloonParaglider]: IconName.Balloon,
  [TMovementTypeGroup.airplaneHelicopter]: IconName.Airplane,
  [TMovementTypeGroup.boatKayakSup]: IconName.Kayak,
  [TMovementTypeGroup.yachtSpeedboat]: IconName.Yacht,
  [TMovementType.other]: IconName.Ufo,
};
